import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useApp } from '../store';
import { displayName, initials } from '../people';
import VendorTag from './VendorTag';
import { applyMention, matchPeople, mentionFragment, resolveMentions, splitMentions } from '../mentions';
import { listNewline } from '../list-autoformat';
import { MentionEditable, type MentionEditableHandle } from './MentionEditable';

/**
 * ONE MENTION-AWARE FIELD AND ONE MENTION-AWARE BODY, FOR THE WHOLE APP.
 *
 * These were born inside the card feed and are now shared, so "@ to mention
 * someone" works the same in every place a person writes — the card composer,
 * every reply box, the in-place editor, and the My Work inbox reply — and so an
 * @name is tinted the same way wherever a written line is read back. One picker,
 * one renderer: a change to how a mention is composed or shown is a change made
 * once.
 */

// A small mention-aware input. It owns its own picker and keeps Escape from
// closing the card while the picker is open. `multiline` renders a textarea (for
// the editor, where an entry can run to several lines); `autoGrow` starts that
// textarea one line tall and grows it to fit as you type (the composer and reply,
// which open small but must wrap a long line rather than scroll it sideways — a
// single-line field cannot wrap, and the highlight mirror drifts out of step with
// the caret once it scrolls across). `onCancel`, when given, is what Escape does
// once the picker is closed — the editor bows out and keeps the entry's words,
// where a composer or reply just steps out of the field.
export function MentionInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  className,
  autoFocus,
  multiline,
  autoGrow,
  onCancel,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  className?: string;
  autoFocus?: boolean;
  multiline?: boolean;
  autoGrow?: boolean;
  onCancel?: () => void;
  ariaLabel?: string;
}) {
  const profiles = useApp((s) => s.profiles);
  const myEmail = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  const emails = useApp((s) => s.access.emails);
  // Compare case-insensitively: access lists store addresses as typed, so a
  // capitalised copy of your own address would otherwise slip this filter and
  // let you @-mention — and be notified about — yourself (F11, the identity
  // match-casing seam). Each address stays in its own case for display.
  const others = emails.filter((e) => e.toLowerCase() !== myEmail);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Where the caret is. The picker reads what is being typed AT THE CARET, not at
  // the end of the field, so a mention can be started anywhere in a sentence.
  const [caret, setCaret] = useState(value.length);
  // After completing a mention the caret belongs right after the inserted name;
  // the new value arrives through onChange and re-renders, so the position is
  // stashed and applied once the field holds that value.
  const pendingCaret = useRef<number | null>(null);
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  // The multiline surface is a contenteditable (see MentionEditable); its handle
  // lets this component read the selection for a Shift+Enter newline. The
  // single-line path stays the <input> above, reached through fieldRef.
  const editorRef = useRef<MentionEditableHandle | null>(null);
  // The highlight layer behind the field — see the mirror below.
  const mirrorRef = useRef<HTMLDivElement>(null);
  const frag = mentionFragment(value, caret) ?? '';
  // What the field's text looks like broken into plain runs and mention runs, so
  // the mirror can tint the @mentions. resolveMentions turns the composed
  // `@Name` into the stored `@address` and splitMentions reads the recognised
  // ones back — so exactly the mentions that will be saved are the ones tinted,
  // and a half-typed or unknown `@name` stays plain until it resolves.
  const runs = splitMentions(resolveMentions(value, others, profiles), profiles);
  // Keep the mirror scrolled in lock-step with the field it sits behind.
  const syncScroll = () => {
    const el = fieldRef.current;
    const mir = mirrorRef.current;
    if (el && mir) {
      mir.scrollTop = el.scrollTop;
      mir.scrollLeft = el.scrollLeft;
    }
  };
  // Grow an auto-grow textarea to fit its text. Height is reset to let it shrink,
  // then set to the content height PLUS the borders — box-sizing is border-box, so
  // the content height alone would come up a border short and leave a stray
  // scrollbar. A max-height in CSS caps it and turns the scrollbar on past that.
  const autoSize = () => {
    if (!autoGrow) return;
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    el.style.height = `${el.scrollHeight + border}px`;
  };
  const matches = matchPeople(frag, others, profiles);
  // The highlighted row, kept in range if the list shrinks under it.
  const activeIdx = matches.length ? Math.min(active, matches.length - 1) : 0;

  // The highlight returns to the top whenever the typed fragment changes or the
  // picker opens, so arrow-keying always starts from a known row.
  useEffect(() => {
    setActive(0);
  }, [frag, open]);

  // Open with the caret at the END of whatever is already in the field, so
  // "@ to mention someone" works straight away: in the editor the entry's words
  // are already there, and a caret left at the start made a typed "@" land in
  // front of them, where the picker — which looks for an "@…" running to the end
  // — could never see it. The empty composer is unaffected.
  useEffect(() => {
    if (!autoFocus) return;
    const el = fieldRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    try {
      el.setSelectionRange(end, end);
    } catch {
      /* some input types carry no text selection; focus alone is enough */
    }
    setCaret(end);
    // Mount only — this positions the caret when the field first opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Put the caret back where it belongs after a completed mention — right after
  // the name just inserted — once the field is showing the new value.
  useEffect(() => {
    if (pendingCaret.current == null) return;
    const el = fieldRef.current;
    const pos = pendingCaret.current;
    pendingCaret.current = null;
    if (!el) return;
    el.focus();
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      /* ignore — focus alone is enough */
    }
    setCaret(pos);
  }, [value]);

  // Re-size (auto-grow) then re-align the mirror whenever the text changes — the
  // field can grow a line, wrap, or scroll, and the mirror must track all three.
  useEffect(() => {
    autoSize();
    syncScroll();
    // autoSize/syncScroll read live refs; value is the only trigger that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close on a click anywhere outside the field and its picker, the way every
  // other menu in the app does (mousedown, scoped to this wrap). Escape already
  // closes it; this is the click-away half of the same rule.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Insert the picked person in place of the half-typed `@word` at the caret, and
  // remember where the caret should land (just past the inserted name).
  const complete = (email: string) => {
    const after = value.slice(caret);
    const next = applyMention(value, email, profiles, caret);
    pendingCaret.current = next.length - after.length;
    onChange(next);
    setOpen(false);
  };
  const onFieldChange = (v: string, pos: number) => {
    onChange(v);
    setCaret(pos);
    setOpen(mentionFragment(v, pos) !== null);
  };
  // Keep the caret in step when it moves without typing — a click, an arrow key,
  // a selection — so the picker reads the right place and reopens if the caret
  // lands back inside a half-typed mention.
  const trackCaret = (el: HTMLTextAreaElement | HTMLInputElement) => {
    const pos = el.selectionStart ?? el.value.length;
    setCaret(pos);
    setOpen(mentionFragment(el.value, pos) !== null);
  };
  // The contenteditable reports its caret as a plain-text offset, with the LIVE
  // value — not the React `value`, which is a tick stale right after a keystroke,
  // so testing the @fragment against it would flicker the picker shut.
  const trackCaretPos = (live: string, pos: number) => {
    setCaret(pos);
    setOpen(mentionFragment(live, pos) !== null);
  };
  const onFieldKeyDown = (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // The picker closes first, and must not also close the card.
      if (open) {
        e.stopPropagation();
        setOpen(false);
        return;
      }
      // The editor hands a way to bow out that keeps the entry's words; a
      // composer or reply has no undo, so a typed-but-unsent line is real work
      // and Escape steps out of the field rather than letting the window handler
      // bin the draft.
      if (onCancel) {
        e.stopPropagation();
        onCancel();
        return;
      }
      if (value.trim() !== '') {
        e.stopPropagation();
        e.currentTarget.blur();
      }
      return;
    }
    // Arrow keys move the highlight through the picker, wrapping top-to-bottom,
    // so the whole list is reachable from the keyboard — not just the top match
    // (F12/L82: before this, arrows did nothing and Enter/Tab always took #0).
    if (open && matches.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => {
        const from = Math.min(a, matches.length - 1);
        return (from + delta + matches.length) % matches.length;
      });
      return;
    }
    // ⌘/Ctrl+Enter posts — the deliberate send. In the multiline box Enter now
    // makes a new line and carries a list on, exactly as the card's "What is this
    // about?" and description boxes do, so continuing a list is ONE action across
    // the app instead of Enter in one place and Shift+Enter in another. Sending
    // therefore moves to a key of its own (and the Post/Save/Reply button). Checked
    // before the picker-complete so it posts even with the mention picker open.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setOpen(false);
      onSubmit();
      return;
    }
    // Tab or Enter takes the HIGHLIGHTED match while the picker is open.
    if ((e.key === 'Tab' || e.key === 'Enter') && open && matches[activeIdx]) {
      e.preventDefault();
      complete(matches[activeIdx]);
      return;
    }
    // A single-line field has no newline, so there Enter still sends.
    if (e.key === 'Enter' && !multiline && !open) {
      e.preventDefault();
      onSubmit();
      return;
    }
    // In the multiline box Enter carries a list on (or ends an empty item), and
    // Shift+Enter is a plain line break — the same rule as ListEditable, so the two
    // surfaces match. Handled in the value, because the contenteditable's own Enter
    // would split the blocks the editor manages. Text and caret both come from the
    // live DOM: the `value` prop can trail it by a tick after a keystroke, and a
    // newline judged on prop-text with a DOM-caret leaves a just-typed "1. item"
    // uncontinued (see MentionEditableHandle.value).
    if (e.key === 'Enter' && multiline && !open) {
      e.preventDefault();
      const live = editorRef.current?.value() ?? value;
      const sel = editorRef.current?.selection() ?? { start: caret, end: caret };
      const edit = !e.shiftKey && sel.start === sel.end ? listNewline(live, sel.start, sel.end) : null;
      if (edit) {
        pendingCaret.current = edit.caret;
        onChange(edit.value);
      } else {
        const next = live.slice(0, sel.start) + '\n' + live.slice(sel.end);
        pendingCaret.current = sel.start + 1;
        onChange(next);
      }
      setOpen(false);
      return;
    }
  };

  const base = className ?? 'ctl';
  return (
    <div className="feed-mentionwrap" ref={wrapRef}>
      {multiline ? (
        /* The multiline surface is one contenteditable — the caret lives in the
           same element that draws the text, so there is no mirror to keep in step,
           and a wrapped list item can hang its text under its first letter while
           you type. See MentionEditable. */
        <MentionEditable
          value={value}
          tint={(line) => splitMentions(resolveMentions(line, others, profiles), profiles)}
          placeholder={placeholder}
          className={base}
          autoFocus={autoFocus}
          ariaLabel={ariaLabel}
          listId={listId}
          open={open}
          matchesLen={matches.length}
          activeIdx={activeIdx}
          caretRef={pendingCaret}
          onInput={(v, pos) => onFieldChange(v, pos)}
          onCaret={(v, pos) => trackCaretPos(v, pos)}
          onKeyDown={onFieldKeyDown}
          handleRef={editorRef}
        />
      ) : (
        <>
          {/* The highlight layer, for the single-line field only. It carries the
              SAME class as the field so its box lines up to the pixel; it shows the
              text with @mentions tinted while the field on top has transparent
              text, so the caret is the field's and the colour is the mirror's.
              aria-hidden — the input carries the value a screen reader reads. */}
          <div className={`${base} mention-mirror is-single`} aria-hidden="true" ref={mirrorRef}>
            {runs.map((part, i) =>
              part.mention ? (
                <span key={i} className="mention">
                  {part.text}
                </span>
              ) : (
                <span key={i}>{part.text}</span>
              )
            )}
            {'​'}
          </div>
          <input
            ref={(el) => {
              fieldRef.current = el;
            }}
            className={`${base} mention-field`}
            placeholder={placeholder}
            value={value}
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && matches.length ? `${listId}-opt-${activeIdx}` : undefined}
            onChange={(e) => onFieldChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onKeyDown={onFieldKeyDown}
            onKeyUp={(e) => trackCaret(e.currentTarget)}
            onClick={(e) => trackCaret(e.currentTarget)}
            onSelect={(e) => trackCaret(e.currentTarget)}
            onScroll={syncScroll}
          />
        </>
      )}
      {open && (
        <div className="mention-picker" id={listId} role="listbox">
          {matches.map((e, i) => (
            <button
              key={e}
              type="button"
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              className={`mention-option${i === activeIdx ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => complete(e)}
            >
              <span className="comment-av">{initials(e, profiles)}</span>
              <span className="mention-name">{displayName(e, profiles)}</span>
              <VendorTag who={e} profiles={profiles} compact />
              <span className="mention-addr">{e}</span>
            </button>
          ))}
          {matches.length === 0 && (
            <div className="hint mention-empty">
              {others.length === 0 ? 'Nobody else is on this board yet.' : 'Nobody here by that name.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// A mention-carrying body is now rendered by IssueText / IssueTextInline with
// `mentions` set — one renderer for the feed, My Work and the card's own text,
// so markdown (lists, bold, references) and @mention tinting always agree.
